import React, { useContext, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { FaStar } from "react-icons/fa";
import productData from "../Datas/productData";
import Footer from "./Footer";
import "../css/productdetails.scss";
import { CartContext } from "../Component/CartContext";
import Header from "./Header";

function formatDetailPrice(price) {
  const n = Number.parseFloat(String(price), 10);
  if (Number.isNaN(n)) return `£${price}`;
  return `£${Math.round(n)}`;
}

const SWATCH_STYLES = {
  "Light Blue": "#a4c2d3",
  Grey: "#8e918f",
  Cream: "#f6ebd8",
  Natural: "#dcc8b6",
  Tan: "#b38c64",
  Khaki: "#b2a98f",
  "Navy Print": "#2b3b5c",
  Beige: "#d9cfc0",
  Navy: "#2a3a52",
  "Light Grey": "#d1d3d4",
  White: "#f9f9f9",
  "Natural Stripe": "repeating-linear-gradient(45deg, #dcc8b6, #dcc8b6 4px, #f9f9f9 4px, #f9f9f9 8px)",
  "Floral Print": "#d4a5a5",
  "Navy Stripe": "repeating-linear-gradient(45deg, #2a3a52, #2a3a52 4px, #f9f9f9 4px, #f9f9f9 8px)",
  "Red Stripe": "repeating-linear-gradient(45deg, #b0413e, #b0413e 4px, #f9f9f9 4px, #f9f9f9 8px)",
  "Pink Stripe": "repeating-linear-gradient(45deg, #e0aeb1, #e0aeb1 4px, #f9f9f9 4px, #f9f9f9 8px)",
  Red: "#b0413e",
  Orange: "#d97a46",
  Gingham: "#4a4a4a",
  Plaid: "#6e4b4b",
  Burgundy: "#7B263D",
  Caramel: "#B77A4A",
  Black: "#1D1B1A",
  Hazel: "#9A6B43",
  Plum: "#68405C",
  Chocolate: "#4A2E25",
  "Glossy black": "#141414",
  Amber: "#B56F32",
  Sage: "#98A28B",
};

function swatchBackground(colorName) {
  const solid = SWATCH_STYLES[colorName];
  if (solid) return { background: solid };
  return {
    background: `linear-gradient(135deg, #e0e0e0 0%, #c0c0c0 100%)`,
  };
}

function StarRating({ rating, reviewCount }) {
  const value = Number.parseFloat(rating, 10) || 0;
  const filledCount = Math.min(5, Math.round(value));

  return (
    <div
      className="product-detail__rating"
      aria-label={`${rating} out of 5 stars`}
    >
      <span className="product-detail__stars">
        {[1, 2, 3, 4, 5].map((i) => (
          <FaStar
            key={i}
            className={`product-detail__star ${
              i <= filledCount ? "is-filled" : ""
            }`}
            aria-hidden="true"
          />
        ))}
      </span>
      <span className="product-detail__review-count">
        {reviewCount} reviews
      </span>
    </div>
  );
}

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const product = productData.find(
    (item) => item.id === Number.parseInt(id, 10)
  );
  const { addToCart } = useContext(CartContext);

  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [selectedImage, setSelectedImage] = useState(0);
  const [detailsOpen, setDetailsOpen] = useState(true);

  useEffect(() => {
    if (!product) return;
    setSelectedSize("");
    setSelectedColor(product.colors?.[0] || "");
    setSelectedImage(0);
    setDetailsOpen(true);
  }, [id, product]);

  const relatedProducts = useMemo(() => {
    if (!product) return [];
    const taken = new Set([product.id]);
    const out = [];
    for (const p of productData) {
      if (out.length >= 3) break;
      if (taken.has(p.id)) continue;
      if (p.category === product.category) {
        out.push(p);
        taken.add(p.id);
      }
    }
    for (const p of productData) {
      if (out.length >= 3) break;
      if (taken.has(p.id)) continue;
      out.push(p);
      taken.add(p.id);
    }
    return out;
  }, [product]);

  const toTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (!product) {
    return (
      <div className="product-detail">
        <Header />
        <div className="product-detail__not-found">
          <h2>Product not found</h2>
          <Link onClick={toTop} to="/shop">
            Back to Shop
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const images = product.images?.length ? product.images : [product.image];
  const shortDescription = product.description?.[0] || "";
  const detailParagraphs = product.description?.slice(1) || [];
  const features = product.Features || [];

  const handleAddToCart = () => {
    if (!selectedSize) return;
    addToCart(product.id, 1, selectedSize, selectedColor);
    navigate("/cartpage");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const mainSrc = images[Math.min(selectedImage, images.length - 1)];

  return (
    <div className="product-detail">
      <Header />

      <main className="product-detail__main" id="MainContent">
        <div className="product-detail__layout">
          <div className="product-detail__gallery">
            <div className="product-detail__main-shot">
              <img
                src={mainSrc}
                alt={product.name}
                className="product-detail__main-img"
                loading="eager"
                decoding="async"
              />
            </div>
            {images.length > 1 ? (
              <div
                className="product-detail__thumbs"
                role="tablist"
                aria-label="Product images"
              >
                {images.map((src, idx) => (
                  <button
                    key={src}
                    type="button"
                    role="tab"
                    aria-selected={selectedImage === idx}
                    className={`product-detail__thumb ${
                      selectedImage === idx ? "is-active" : ""
                    }`}
                    onClick={() => setSelectedImage(idx)}
                  >
                    <img src={src} alt="" loading="lazy" decoding="async" />
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          <div className="product-detail__sidebar">
            <h1 className="product-detail__title">{product.name}</h1>
            <p className="product-detail__price">
              {formatDetailPrice(product.price)}
            </p>
            <StarRating rating={product.rating} reviewCount={product.reviews} />

            <p className="product-detail__lead">{shortDescription}</p>

            <div className="product-detail__divider" />

            <div className="product-detail__field">
              <p className="product-detail__label">
                Color:{" "}
                <span className="product-detail__label-value">
                  {selectedColor}
                </span>
              </p>
              <div
                className="product-detail__swatches"
                role="listbox"
                aria-label="Color"
              >
                {product.colors?.map((c) => (
                  <button
                    key={c}
                    type="button"
                    role="option"
                    aria-selected={selectedColor === c}
                    className={`product-detail__swatch ${
                      selectedColor === c ? "is-selected" : ""
                    }`}
                    style={swatchBackground(c)}
                    title={c}
                    onClick={() => setSelectedColor(c)}
                  />
                ))}
              </div>
            </div>

            <div className="product-detail__divider" />

            <div className="product-detail__field">
              <div className="product-detail__size-row">
                <span className="product-detail__label">Size:</span>
              </div>
              <div className="product-detail__sizes" id="size-chart">
                {product.size?.map((sz) => (
                  <button
                    key={sz}
                    type="button"
                    className={`product-detail__size-btn ${
                      selectedSize === sz ? "is-selected" : ""
                    }`}
                    onClick={() => setSelectedSize(sz)}
                  >
                    {sz}
                  </button>
                ))}
              </div>
            </div>

            <button
              type="button"
              className={`product-detail__cta ${
                selectedSize ? "product-detail__cta--active" : ""
              }`}
              onClick={handleAddToCart}
              disabled={!selectedSize}
            >
              {selectedSize ? "ADD TO BAG" : "SELECT A SIZE"}
            </button>

            <p className="product-detail__shipping-note">
              Free Shipping On £75+ Orders. Free Returns for 365 Days.
            </p>

            <div className="product-detail__divider product-detail__divider--spaced" />

            <div className="product-detail__accordion">
              <button
                type="button"
                className="product-detail__accordion-trigger"
                aria-expanded={detailsOpen}
                onClick={() => setDetailsOpen((o) => !o)}
              >
                <span>Details</span>
                <svg
                  className={`product-detail__chevron ${
                    detailsOpen ? "is-open" : ""
                  }`}
                  width="14"
                  height="8"
                  viewBox="0 0 14 8"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M1 1.5L7 6.5L13 1.5"
                    stroke="currentColor"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              {detailsOpen ? (
                <div className="product-detail__accordion-panel">
                  {detailParagraphs.map((para, i) => (
                    <p key={i} className="product-detail__detail-text">
                      {para}
                    </p>
                  ))}
                  <ul className="product-detail__feature-list">
                    {features.map((f) => (
                      <li key={f}>{f}</li>
                    ))}
                  </ul>
                  <p className="product-detail__material">
                    <strong>Materials:</strong> {product.material}
                  </p>
                  <p className="product-detail__fit-note">
                    <strong>Fit:</strong>{" "}
                    {product.attributes?.fit ||
                      "True to size. Refer to the Size Chart for measurements."}
                  </p>
                </div>
              ) : null}
            </div>

            {relatedProducts.length > 0 ? (
              <section
                className="product-detail__pairs"
                aria-labelledby="pairs-heading"
              >
                <div className="product-detail__divider" />
                <h2 id="pairs-heading" className="product-detail__pairs-title">
                  Pairs Well With
                </h2>
                <div className="product-detail__pairs-grid">
                  {relatedProducts.map((p) => (
                    <Link
                      key={p.id}
                      className="product-detail__pair-card"
                      to={`/shopDetails/${p.id}`}
                      onClick={toTop}
                    >
                      <div className="product-detail__pair-img-wrap">
                        <img src={p.image} alt={p.name} loading="lazy" />
                      </div>
                      <h3 className="product-detail__pair-name">{p.name}</h3>
                      <p className="product-detail__pair-price">
                        {formatDetailPrice(p.price)}
                      </p>
                      <p className="product-detail__pair-colors">
                        Comes in {p.colors?.length || 1} color
                        {(p.colors?.length || 1) !== 1 ? "s" : ""}
                      </p>
                    </Link>
                  ))}
                </div>
                <div
                  className="product-detail__pairs-scroll"
                  aria-hidden="true"
                >
                  <span className="product-detail__pairs-scroll-bar" />
                </div>
              </section>
            ) : null}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ProductDetail;
